//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { Alert } from '@components/atoms/alert/Alert';
import { InvisibleButton } from '@components/atoms/button/Button';
import { BodyText } from '@components/atoms/text/Text';
import markRightsSvg from '@images/uia/uia-approve-rights.svg';
import requestRightsSvg from '@images/uia/uia-request-rights.svg';
import skipRightsSvg from '@images/uia/uia-skip-rights.svg';
import Cuid from '@models/Cuid';
import { SocialNetwork } from '@models/SocialNetwork';
import { UIARequestRights } from '@uia/rights/UIARequestRightsForm';
import { UIARightsMarkApprovedForm } from '@uia/rights/UIARightsMarkApprovedForm';
import { UIARightsMarkSkippedForm } from '@uia/rights/UIARightsMarkSkippedForm';
import { UIA_RIGHTS_FLOW_TRACKING_IDS } from '@uia/rights/constants/uiaRightsConstants';
import {
  getIsRenderedWithinAnotherWorkflow,
  getIsTikTokMentionedRightsEnabled,
} from '@uia/rights/selectors/getUIARights';

type RightsOption = 'request' | 'mark' | 'skip';

interface Props {
  cuid: Cuid;
  isBrandOwned: boolean;
  onCloseModal: () => void;
  onUpdateRights: () => void;
  socialNetwork?: SocialNetwork;
  onRightsSubOptionSelected: (option: string) => void;
  shouldShowOptions: boolean;
}

export const UIARightsOptions = ({
  cuid,
  isBrandOwned,
  onCloseModal,
  onRightsSubOptionSelected,
  onUpdateRights,
  shouldShowOptions,
  socialNetwork,
}: Props) => {
  const isRenderedWithinAnotherWorkflow = useSelector(
    getIsRenderedWithinAnotherWorkflow,
  );

  const isTikTokMentionedRightsEnabled = useSelector(
    getIsTikTokMentionedRightsEnabled,
  );

  const [prevShouldShowOptions, setPrevShouldShowOptions] =
    useState(shouldShowOptions);
  const [selectedOption, setSelectedOption] = useState<
    RightsOption | undefined
  >(undefined);

  if (shouldShowOptions !== prevShouldShowOptions) {
    setPrevShouldShowOptions(shouldShowOptions);

    if (shouldShowOptions) {
      // set to undefined to return to showing all options rather than a specific option
      setSelectedOption(undefined);
    }
  }

  const handleOptionSelect = (option: RightsOption) => {
    setSelectedOption(option);

    // When in another workflow, like Galleries, we need to communicate that a sub option is selected
    // so that the previous button of the workflow goes back to the rights options before going back to previous
    // steps in the flow
    if (isRenderedWithinAnotherWorkflow) {
      onRightsSubOptionSelected(option);
    }
  };

  const partialCuid = Cuid.toPartial(cuid);

  if (!selectedOption) {
    return (
      <RightsOptionsContainer>
        {/* For brand owned content with expired rights, we need to show the skip rights and mark as approved options only */}
        {!isBrandOwned &&
          (socialNetwork === SocialNetwork.Instagram ||
            (socialNetwork === SocialNetwork.TikTok &&
              isTikTokMentionedRightsEnabled)) && (
            <RightsOptionItem>
              <RightsOption
                onClick={() => handleOptionSelect('request')}
                trackingId={UIA_RIGHTS_FLOW_TRACKING_IDS.REQUEST_RIGHTS_OPTION}
              >
                <RightsOptionContainer>
                  <RightsOptionImage alt='' src={requestRightsSvg} />
                  <StyledText>Request Rights</StyledText>
                </RightsOptionContainer>
              </RightsOption>
            </RightsOptionItem>
          )}

        <RightsOptionItem>
          <RightsOption
            onClick={() => handleOptionSelect('mark')}
            trackingId={
              UIA_RIGHTS_FLOW_TRACKING_IDS.MARK_APPROVED_RIGHTS_OPTION
            }
          >
            <RightsOptionContainer>
              <RightsOptionImage alt='' src={markRightsSvg} />
              <StyledText>Mark as approved</StyledText>
            </RightsOptionContainer>
          </RightsOption>
        </RightsOptionItem>

        <RightsOptionItem>
          <RightsOption
            onClick={() => handleOptionSelect('skip')}
            trackingId={UIA_RIGHTS_FLOW_TRACKING_IDS.SKIP_RIGHTS_OPTION}
          >
            <RightsOptionContainer>
              <RightsOptionImage alt='' src={skipRightsSvg} />
              <StyledText>Skip rights request</StyledText>
            </RightsOptionContainer>
          </RightsOption>
        </RightsOptionItem>
      </RightsOptionsContainer>
    );
  } else if (selectedOption === 'request') {
    return (
      <UIARequestRights
        cuid={cuid}
        onCloseModal={onCloseModal}
        onPrevious={() => setSelectedOption(undefined)}
      />
    );
  } else if (selectedOption === 'mark') {
    return (
      <UIARightsMarkApprovedForm
        handleReturnToRightsOptions={() => setSelectedOption(undefined)}
        onUpdateRights={onUpdateRights}
        partialCuid={partialCuid}
      />
    );
  } else if (selectedOption === 'skip') {
    return (
      <UIARightsMarkSkippedForm
        handleReturnToRightsOptions={() => setSelectedOption(undefined)}
        onUpdateRights={onUpdateRights}
        partialCuid={partialCuid}
      />
    );
  } else {
    return (
      <StyledAlert type='error'>
        Something went wrong when loading rights. Please try again or contact
        Bazaarvoice Support.
      </StyledAlert>
    );
  }
};

const RightsOptionsContainer = styled.ul`
  display: flex;
  align-items: center;
  flex-direction: column;
  height: 100%;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const StyledText = styled(BodyText)(
  ({ theme }) => `
  display: flex;
  align-items: center;
  margin-left: ${theme.spacings[3]};
  font-size: ${theme.fontSizes[3]};
  color: ${theme.colors.teal1};
`,
);

const RightsOptionItem = styled.li(
  ({ theme }) => `
    display: flex;
    width: 100%;
    border-bottom: ${theme.borderWidths[1]} solid ${theme.colors.gray1};
  `,
);

const RightsOptionImage = styled.img(
  ({ theme }) => `
  width: 100px;
  margin-left: ${theme.spacings[6]};
`,
);

const RightsOption = styled(InvisibleButton)(
  ({ theme }) => `
  height: 100%;
  width: 100%;
  padding: ${theme.spacings[3]} 0;

  button {
    justify-content: unset;
  }
  
  button div {
    height: 100px;
  }

`,
);

const RightsOptionContainer = styled.div`
  display: flex;
`;

const StyledAlert = styled(Alert)(
  ({ theme }) => `
    margin-top: ${theme.spacings[7]};
  `,
);
